import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ElementType, FC, MouseEvent, MouseEventHandler } from 'react';

import {
  useTheme,
  useArrows,
  useConsolidatedRef,
  useDirection,
  useUID,
  useI18n,
  useEscape
} from '../../hooks';
import { isValidElement } from '../../utils';
import ExpandCollapse from '../ExpandCollapse';
import BareButton from '../Button/BareButton';
import Flex from '../Flex';
import Icon, { registerIcon } from '../Icon';
import Text from '../Text';
import Button from '../Button';
import type { OmitStrict } from '../../types';
import type { PopoverProps } from '../Popover';
import Link from '../Link';
import * as timesIcon from '../Icon/icons/times.icon';
import * as checkIcon from '../Icon/icons/check.icon';
import * as caretLeftIcon from '../Icon/icons/caret-left.icon';
import * as caretRightIcon from '../Icon/icons/caret-right.icon';

import NavigationListItemWrapper from './NavigationListItemWrapper';
import type {
  NavListItemProps,
  NavListItemWrapperProps,
  NavListProps,
  NavMenuProps
} from './AppShell.types';
import {
  StyledNavList,
  StyledNestedNavList,
  StyledNavItemIconBox,
  StyledNavListItem,
  StyledNavListItemExpandCollapse,
  StyledCaseClose,
  StyledSingleSelectIconBox,
  StyledNavListItemVisualContainer,
  StyledNavListItemGroupIndicator
} from './AppShell.styles';
import AppShellContext from './AppShellContext';

registerIcon(timesIcon, checkIcon, caretLeftIcon, caretRightIcon);

const NavigationMenu: FC<NavMenuProps> = props => {
  const { navOpen } = useContext(AppShellContext);
  const {
    visual,
    primary,
    secondary,
    href,
    onClick,
    items,
    onDismiss,
    actions,
    forwardedRef,
    ...restProps
  } = props;

  const uid = useUID();

  const { navState } = useContext(AppShellContext);
  const [collapsed, setCollapsed] = useState(true);

  const toggleCollapsed = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setCollapsed(state => !state);
    },
    [setCollapsed]
  );

  const itemRef = useConsolidatedRef(forwardedRef);

  const menuRef = useRef<HTMLLIElement>(null);

  useArrows(menuRef, { selector: '[role="menuitem"]', cycle: true });

  useEscape(() => {
    setCollapsed(true);
    itemRef.current?.focus();
  }, menuRef);

  const { end } = useDirection();

  return (
    <StyledNavListItem {...restProps} key={primary} nestedListCollapsed={collapsed} ref={menuRef}>
      <BareButton
        id={uid}
        ref={itemRef}
        aria-expanded={!collapsed}
        aria-controls={`${uid}-menu`}
        aria-label={primary}
        onClick={toggleCollapsed}
      >
        {visual && <StyledNavItemIconBox>{visual}</StyledNavItemIconBox>}

        {secondary ? (
          <Flex container={{ direction: 'column', alignItems: 'start' }}>
            <span>{primary}</span>
            <Text variant='secondary'>{secondary}</Text>
          </Flex>
        ) : (
          <span>{primary}</span>
        )}

        {navState === 'open' && <StyledNavListItemExpandCollapse name={`caret-${end}`} />}
      </BareButton>

      {/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
      <NavigationList
        id={`${uid}-menu`}
        nestedList
        items={items}
        collapsed={!navOpen || collapsed}
      />
    </StyledNavListItem>
  );
};

export const NavigationListItem: FC<NavListItemProps> = ({
  visual,
  primary,
  secondary,
  collapsedItemInfo,
  href,
  onClick,
  items,
  collapseItems = false,
  onDismiss,
  actions,
  forwardedRef,
  isMenuItem,
  mobileNavBar,
  count,
  singleSelect,
  selected,
  'aria-labelledby': ariaLabelledBy,
  'aria-current': ariaCurrent,
  ...restProps
}) => {
  const { navOpen: navOpenValue, navState } = useContext(AppShellContext);
  const navOpen = mobileNavBar ? false : navOpenValue;
  const t = useI18n();
  const listItemRef = useConsolidatedRef(forwardedRef);
  const [menuState, setMenuState] = useState('');
  const {
    components: {
      'app-shell': {
        nav: { detached }
      }
    }
  } = useTheme();

  const hasItems =
    (Array.isArray(items) && items.length > 0) || (Array.isArray(actions) && actions.length > 0);
  const compProps: {
    href?: string;
    onClick?: MouseEventHandler<HTMLAnchorElement | HTMLButtonElement | HTMLDivElement>;
    actions?: NavListItemProps['actions'];
    popover?: OmitStrict<PopoverProps, 'show' | 'target' | 'children'>;
    'aria-label'?: string;
  } = {
    onClick,
    href
  };

  let caseItems: NavListItemWrapperProps['items'] | undefined;
  if (onDismiss) {
    caseItems = [
      {
        primary: t('go_to_noun', [primary]),
        onClick
      },
      {
        primary: t('dismiss_case'),
        onClick: onDismiss
      }
    ];
  }

  let Component: ElementType;

  if (href) {
    Component = Link;
  } else {
    Component = BareButton;
  }

  const shouldShowMenuAttributes = hasItems || (onDismiss && !navOpen);

  useEffect(() => {
    if (!navOpen && actions) {
      const node = document.getElementsByTagName('body')[0];
      const clickEvent = document.createEvent('MouseEvent');
      clickEvent.initEvent('mousedown', true, true);
      node.dispatchEvent(clickEvent);
    }
    if (navOpen && isMenuItem && listItemRef.current?.parentElement?.matches(':first-child'))
      listItemRef.current?.focus();
  }, [navOpen]);

  const { end } = useDirection();
  const primaryId = useUID();

  return (
    <StyledNavListItem {...restProps} key={primary} singleSelect={singleSelect} nestedListCollapsed>
      <NavigationListItemWrapper
        tooltipLabel={secondary ? `${primary} ${secondary}` : primary}
        label={primary}
        childElementRef={listItemRef}
        items={actions || items || caseItems}
        onMenuToggle={setMenuState}
        mobileNavBar={mobileNavBar}
      >
        <Component
          {...compProps}
          ref={listItemRef}
          {...(navOpen && isMenuItem ? { tabIndex: -1, role: 'menuitem' } : {})}
          aria-labelledby={`${primaryId} ${count ? `${primary}-count` : ''} ${ariaLabelledBy ?? ''}`}
          {...(shouldShowMenuAttributes && {
            'aria-haspopup': 'menu',
            'aria-expanded': menuState === 'open'
          })}
          aria-current={ariaCurrent}
        >
          {singleSelect && (
            <Flex
              container={{ justify: 'center', alignItems: 'center' }}
              as={StyledSingleSelectIconBox}
            >
              {selected && <Icon name='check' />}
            </Flex>
          )}
          {visual && (
            <Flex
              container={{ direction: 'column', gap: 0.5 }}
              as={StyledNavListItemVisualContainer}
              isCurrent={ariaCurrent === 'page'}
              navOpen={navOpen}
            >
              <StyledNavItemIconBox>{visual}</StyledNavItemIconBox>
              {!navOpen && collapsedItemInfo && (
                <Text variant='secondary'>{collapsedItemInfo}</Text>
              )}
              {hasItems && navState === 'closed' && (
                <StyledNavListItemGroupIndicator name={`caret-${end}`} />
              )}
            </Flex>
          )}

          {secondary ? (
            <Flex
              container={{ direction: 'column', alignItems: 'start' }}
              item={{ shrink: !navOpen && detached ? 1 : 0 }}
              as='span'
            >
              <span id={primaryId}>{primary}</span>
              <Text variant='secondary'>{secondary}</Text>
            </Flex>
          ) : (
            <span id={primaryId}>{primary}</span>
          )}

          {(hasItems || actions) && navState === 'open' && !mobileNavBar && (
            <StyledNavListItemExpandCollapse name={`caret-${end}`} />
          )}
        </Component>
      </NavigationListItemWrapper>

      {onDismiss && navOpen && (
        <Button
          icon
          variant='simple'
          as={StyledCaseClose}
          onClick={(e: MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            onDismiss(e);
          }}
          aria-label={t('dismiss_case')}
        >
          <Icon name='times' />
        </Button>
      )}

      {hasItems && (
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        <NavigationList nestedList items={actions || items} collapsed={!navOpen || collapseItems} />
      )}
    </StyledNavListItem>
  );
};

const NavigationList: FC<NavListProps> = ({
  items = [],
  nestedList,
  id,
  collapsed,
  singleSelect,
  mobileNavBar
}) => {
  let Component: ElementType = StyledNavList;
  let ItemComponent: ElementType;
  const { navOpen } = useContext(AppShellContext);

  let fwdProps: {
    as?: ElementType;
    forwardedAs?: ElementType;
    nullWhenCollapsed?: boolean;
    transitionSpeed?: string;
    collapsed: boolean;
  } = {
    collapsed: !!collapsed
  };

  const {
    base: { animation }
  } = useTheme();

  if (!items.length) {
    return null;
  }

  if (nestedList) {
    fwdProps = {
      as: ExpandCollapse,
      forwardedAs: 'ul',
      nullWhenCollapsed: true,
      transitionSpeed: items.length >= 10 ? `calc(2 * ${animation.speed})` : animation.speed,
      ...fwdProps
    };

    Component = StyledNestedNavList;
  }

  return (
    <Component {...(nestedList ? { role: 'menu', id } : {})} {...fwdProps}>
      {items.map(item => {
        if (isValidElement(item)) return item;

        if (item.items && item.items.length > 0 && navOpen && !mobileNavBar) {
          ItemComponent = NavigationMenu;
        } else {
          ItemComponent = NavigationListItem;
        }

        return (
          <ItemComponent
            key={item.id || item.primary}
            isMenuItem={nestedList}
            singleSelect={singleSelect}
            aria-current={item['aria-current']}
            mobileNavBar={mobileNavBar}
            {...item}
          />
        );
      })}
    </Component>
  );
};

export default NavigationList;
